import { uiPreferencesSchema } from "@obsina/domain";
import type { UiPreferences } from "@obsina/domain";
import { CsrfTokenError, csrfTokenCacheFor } from "./csrf";
import { parseControlPlaneSummary, parseHistoryEstimate } from "./domain";
import type {
  ControlPlaneHistoryEstimate,
  ControlPlaneSummary,
  VisibilityClass,
} from "./domain";
import {
  parseCommitmentEvidenceResponse,
  parseOpenCommitmentsResponse,
} from "./commitments";
import type {
  CommitmentEvidenceResult,
  OpenCommitmentsResult,
} from "./commitments";
import {
  parseSharePointDriveList,
  parseSharePointDriveSelection,
  parseSharePointDriveSelectionList,
  parseSharePointSitePage,
} from "./sharepoint";
import type {
  SharePointDrive,
  SharePointDriveSelection,
  SharePointSitePage,
} from "./sharepoint";
import {
  parseMailFolderPage,
  parseMailFolderSelection,
  parseMailFolderSelectionList,
} from "./mail-folders";
import type { MailFolderPage, MailFolderSelection } from "./mail-folders";

export class ControlPlaneError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = "ControlPlaneError";
  }
}

export const CONTROL_PLANE_REQUEST_TIMEOUT_MS = 15_000;

async function boundedFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const lifecycleSignal = init.signal;
  const controller = new AbortController();
  let timedOut = false;
  let rejectForAbort: ((reason: unknown) => void) | null = null;
  const aborted = new Promise<never>((_resolve, reject) => {
    rejectForAbort = reject;
  });
  const rejectAbortedRequest = () => {
    rejectForAbort?.(
      controller.signal.reason ??
        new DOMException("The request was aborted.", "AbortError"),
    );
  };
  const forwardLifecycleAbort = () => {
    controller.abort(
      lifecycleSignal?.reason ??
        new DOMException("The request was aborted.", "AbortError"),
    );
  };

  controller.signal.addEventListener("abort", rejectAbortedRequest, {
    once: true,
  });
  if (lifecycleSignal?.aborted) {
    forwardLifecycleAbort();
  } else {
    lifecycleSignal?.addEventListener("abort", forwardLifecycleAbort, {
      once: true,
    });
  }
  const timeout = globalThis.setTimeout(() => {
    timedOut = true;
    controller.abort(
      new ControlPlaneError("The control-plane request timed out. Try again."),
    );
  }, CONTROL_PLANE_REQUEST_TIMEOUT_MS);

  try {
    return await Promise.race([
      fetch(input, { ...init, signal: controller.signal }),
      aborted,
    ]);
  } catch (reason) {
    if (timedOut) {
      throw new ControlPlaneError(
        "The control-plane request timed out. Try again.",
      );
    }
    if (reason instanceof Error) throw reason;
    throw new ControlPlaneError("The control-plane request failed.");
  } finally {
    globalThis.clearTimeout(timeout);
    controller.signal.removeEventListener("abort", rejectAbortedRequest);
    lifecycleSignal?.removeEventListener("abort", forwardLifecycleAbort);
  }
}

export function normalizeApiBase(value: string | undefined): string | null {
  const normalized = value?.trim();
  if (!normalized) return null;
  try {
    const parsed = new URL(normalized);
    const loopback = ["localhost", "127.0.0.1", "::1"].includes(
      parsed.hostname,
    );
    if (
      (parsed.protocol !== "https:" &&
        !(parsed.protocol === "http:" && loopback)) ||
      parsed.username ||
      parsed.password ||
      parsed.pathname !== "/" ||
      parsed.search ||
      parsed.hash
    ) {
      return null;
    }
    return parsed.origin;
  } catch {
    return null;
  }
}

function url(apiBase: string, path: string): string {
  const normalized = normalizeApiBase(apiBase);
  if (!normalized) {
    throw new ControlPlaneError("The control-plane API origin is invalid.");
  }
  return `${normalized}${path.startsWith("/") ? path : `/${path}`}`;
}

export async function establishApplicationSession(
  apiBase: string,
  supabaseAccessToken: string,
): Promise<void> {
  if (
    supabaseAccessToken.length === 0 ||
    supabaseAccessToken.length > 2_700 ||
    !/^[A-Za-z0-9._~-]+$/.test(supabaseAccessToken)
  ) {
    throw new ControlPlaneError("The application session token is invalid.");
  }
  const response = await boundedFetch(url(apiBase, "/v1/security/session"), {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${supabaseAccessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({}),
  });
  if (!response.ok) throw await controlPlaneResponseError(response);
}

export async function clearApplicationSession(apiBase: string): Promise<void> {
  const response = await boundedFetch(url(apiBase, "/v1/security/session"), {
    method: "DELETE",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({}),
  });
  if (!response.ok) throw await controlPlaneResponseError(response);
}

export async function bootstrapApplicationOwner(
  apiBase: string,
  userId: string,
): Promise<void> {
  const normalizedUserId = userId.trim().toLowerCase();
  if (
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(
      normalizedUserId,
    )
  ) {
    throw new ControlPlaneError(
      "The authenticated user identifier is invalid.",
    );
  }
  await request(apiBase, "/v1/onboarding/bootstrap", {
    method: "POST",
    body: JSON.stringify({
      tenantName: "Obsina Staging",
      tenantSlug: `obsina-staging-${normalizedUserId.slice(0, 8)}`,
    }),
  });
}

/** One organisation the signed-in account may act in. */
export interface TenantMembership {
  readonly tenantId: string;
  readonly tenantName: string | null;
  readonly role: "owner" | "member";
}

const uuidPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

/**
 * Lists the organisations this session may act in. The list is a rendering
 * input only: the API decides what the chosen organisation may read, so a
 * tampered response can name nothing the account is not already a member of.
 */
export async function listTenantMemberships(
  apiBase: string,
  signal?: AbortSignal,
): Promise<readonly TenantMembership[]> {
  const response = await request(apiBase, "/v1/auth/memberships", {
    cache: "no-store",
    signal,
  });
  const payload: unknown = await response.json();
  if (
    typeof payload !== "object" ||
    payload === null ||
    !("memberships" in payload) ||
    !Array.isArray(payload.memberships)
  ) {
    throw new ControlPlaneError("The organisation list was invalid.");
  }
  return payload.memberships.map((candidate: unknown) => {
    if (typeof candidate !== "object" || candidate === null) {
      throw new ControlPlaneError("The organisation list was invalid.");
    }
    const entry = candidate as Record<string, unknown>;
    const tenantId = entry.tenantId;
    const tenantName = entry.tenantName;
    const role = entry.role;
    if (
      typeof tenantId !== "string" ||
      !uuidPattern.test(tenantId) ||
      (tenantName !== null && typeof tenantName !== "string") ||
      (role !== "owner" && role !== "member")
    ) {
      throw new ControlPlaneError("The organisation list was invalid.");
    }
    return { tenantId, tenantName: tenantName?.slice(0, 300) ?? null, role };
  });
}

/**
 * Records which organisation this session acts in. The API re-checks the
 * membership and answers 403 when the account is not an active member, so this
 * call requests a selection rather than asserting one.
 */
export async function selectTenant(
  apiBase: string,
  tenantId: string,
): Promise<void> {
  if (!uuidPattern.test(tenantId)) {
    throw new ControlPlaneError("The organisation identifier is invalid.");
  }
  await request(apiBase, "/v1/auth/tenant", {
    method: "POST",
    body: JSON.stringify({ tenantId }),
  });
}

/**
 * Runs a mutation with the page's shared CSRF token, refreshing once if the API
 * refuses it. The cache lives in csrf.ts so this client, the WhatsApp client and
 * the memory graph all reuse one token instead of one per request.
 */
async function sendWithCsrf(
  apiBase: string,
  perform: (csrf: string) => Promise<Response>,
): Promise<Response> {
  const origin = normalizeApiBase(apiBase);
  if (!origin) {
    throw new ControlPlaneError("The control-plane API origin is invalid.");
  }
  try {
    return await csrfTokenCacheFor(origin).send(perform);
  } catch (error) {
    if (error instanceof CsrfTokenError) {
      throw new ControlPlaneError(error.message, error.status);
    }
    throw error;
  }
}

async function controlPlaneResponseError(
  response: Response,
): Promise<ControlPlaneError> {
  const requestId = response.headers.get("x-request-id");
  let code: string | undefined;
  try {
    const payload: unknown = await response.clone().json();
    if (
      typeof payload === "object" &&
      payload !== null &&
      "error" in payload &&
      typeof payload.error === "string" &&
      /^[a-z][a-z0-9_]{0,127}$/u.test(payload.error)
    ) {
      code = payload.error;
    }
  } catch {
    // The status and request id remain useful when an error body is absent.
  }
  return new ControlPlaneError(
    `The control plane returned ${response.status}.${requestId ? ` Request ${requestId}.` : ""}`,
    response.status,
    code,
  );
}

export function isSharePointOwnerPrivateRequired(reason: unknown): boolean {
  return (
    reason instanceof ControlPlaneError &&
    reason.status === 409 &&
    reason.code === "microsoft_files_owner_private_required"
  );
}

/**
 * The session is valid but belongs to more than one organisation and has not
 * chosen one yet. Distinct from a failed session: signing the user out here
 * would answer a question they were never asked.
 */
export function isTenantSelectionRequired(reason: unknown): boolean {
  return (
    reason instanceof ControlPlaneError &&
    reason.status === 409 &&
    reason.code === "tenant_selection_required"
  );
}

export function isVisibilityTransitionResetRequired(reason: unknown): boolean {
  return (
    reason instanceof ControlPlaneError &&
    reason.status === 409 &&
    reason.code === "visibility_transition_requires_reset"
  );
}

async function request(
  apiBase: string,
  path: string,
  init?: RequestInit,
): Promise<Response> {
  const method = (init?.method ?? "GET").toUpperCase();
  const mutation =
    method !== "GET" && method !== "HEAD" && method !== "OPTIONS";
  const send = (csrf: string | null): Promise<Response> =>
    boundedFetch(url(apiBase, path), {
      ...init,
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...(init?.body ? { "Content-Type": "application/json" } : {}),
        ...(csrf ? { "csrf-token": csrf } : {}),
        ...init?.headers,
      },
    });
  const response = mutation
    ? await sendWithCsrf(apiBase, send)
    : await send(null);
  if (!response.ok) throw await controlPlaneResponseError(response);
  return response;
}

export async function getControlPlaneSummary(
  apiBase: string,
  signal?: AbortSignal,
): Promise<ControlPlaneSummary> {
  const response = await request(
    apiBase,
    "/v1/control-plane/summary?contract=3",
    {
      cache: "no-store",
      signal,
    },
  );
  const payload: unknown = await response.json();
  return parseControlPlaneSummary(payload);
}

/**
 * Loads the authenticated member's compact UI preferences. The opaque browser
 * session identifies the member; no user or tenant identifier is accepted from
 * the browser at this boundary.
 */
export async function getUiPreferences(
  apiBase: string,
  signal?: AbortSignal,
): Promise<UiPreferences> {
  const response = await request(apiBase, "/v1/preferences/ui", {
    cache: "no-store",
    signal,
  });
  return uiPreferencesSchema.parse(await response.json());
}

/**
 * Persists one complete preference snapshot. Sending both fields keeps rapid
 * collapse and resize commits deterministic while the provider serializes them.
 */
export async function saveUiPreferences(
  apiBase: string,
  preferences: UiPreferences,
  signal?: AbortSignal,
): Promise<UiPreferences> {
  const validated = uiPreferencesSchema.parse(preferences);
  const response = await request(apiBase, "/v1/preferences/ui", {
    method: "PATCH",
    body: JSON.stringify(validated),
    signal,
  });
  return uiPreferencesSchema.parse(await response.json());
}

export async function saveVisibility(
  apiBase: string,
  connectionId: string,
  visibilityClass: VisibilityClass,
  historyStartAt: string | null,
): Promise<void> {
  await request(
    apiBase,
    `/v1/control-plane/connections/${encodeURIComponent(connectionId)}/visibility`,
    {
      method: "PATCH",
      body: JSON.stringify({ historyStartAt, visibilityClass }),
    },
  );
}

export async function setServiceEnabled(
  apiBase: string,
  connectionId: string,
  serviceKind: "calendar" | "teams" | "onedrive" | "sharepoint",
  enabled: boolean,
): Promise<void> {
  await request(
    apiBase,
    `/v1/control-plane/connections/${encodeURIComponent(
      connectionId,
    )}/services/${encodeURIComponent(serviceKind)}`,
    {
      method: "PATCH",
      body: JSON.stringify({ enabled }),
    },
  );
}

export async function saveHistoryStart(
  apiBase: string,
  connectionId: string,
  historyStartAt: string | null,
  idempotencyKey: string,
): Promise<{ status: "queued" | "unchanged" }> {
  if (
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(
      idempotencyKey,
    )
  ) {
    throw new ControlPlaneError("The history idempotency key is invalid.");
  }
  const response = await request(
    apiBase,
    `/v1/control-plane/connections/${encodeURIComponent(connectionId)}/history`,
    {
      method: "PATCH",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify({ historyStartAt }),
    },
  );
  const payload: unknown = await response.json();
  if (
    typeof payload !== "object" ||
    payload === null ||
    !("status" in payload) ||
    (payload.status !== "queued" && payload.status !== "unchanged")
  ) {
    throw new ControlPlaneError(
      "The control plane returned an invalid history update status.",
    );
  }
  return { status: payload.status };
}

export async function requestHistoryEstimate(
  apiBase: string,
  connectionId: string,
  historyStartAt: string | null,
  signal?: AbortSignal,
): Promise<ControlPlaneHistoryEstimate> {
  const response = await request(
    apiBase,
    `/v1/control-plane/connections/${encodeURIComponent(connectionId)}/history/estimate`,
    {
      method: "POST",
      body: JSON.stringify({ historyStartAt }),
      signal,
    },
  );
  return parseHistoryEstimate(await response.json());
}

// The UI trigger for this was removed for the autonomous model — the surface
// re-syncs on its own, so nothing calls startSync from the control-plane view.
// It is retained deliberately as the client capability (and its idempotency +
// no-authorization-header invariants stay unit-tested) for a future
// issue-retry path; do not remove it as "dead code".
export async function startSync(
  apiBase: string,
  connectionId: string,
  idempotencyKey: string,
): Promise<void> {
  if (
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(
      idempotencyKey,
    )
  ) {
    throw new ControlPlaneError("The sync idempotency key is invalid.");
  }
  await request(
    apiBase,
    `/v1/control-plane/connections/${encodeURIComponent(connectionId)}/sync`,
    {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
    },
  );
}

function containsControlCharacter(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

/**
 * One opaque Microsoft object id, checked before it is placed in a path. Shared
 * by every Microsoft picker so the same bound is applied once; `subject` only
 * decides which surface an invalid id is reported against.
 */
function microsoftResourceId(
  value: string,
  subject: string,
  label: string,
): string {
  if (
    value.length === 0 ||
    value.length > 2_048 ||
    containsControlCharacter(value) ||
    /[\\/?#]/u.test(value)
  ) {
    throw new ControlPlaneError(`The ${subject} ${label} is invalid.`);
  }
  return value;
}

function sharePointResourceId(value: string, label: string): string {
  return microsoftResourceId(value, "SharePoint", label);
}

function mailFolderResourceId(value: string, label: string): string {
  return microsoftResourceId(value, "mail folder", label);
}

function sharePointConnectionPath(connectionId: string): string {
  return `/v1/control-plane/connections/${encodeURIComponent(
    sharePointResourceId(connectionId, "connection identifier"),
  )}/sharepoint`;
}

/** Loads the libraries this member has explicitly selected for ingestion. */
export async function getSharePointDriveSelections(
  apiBase: string,
  connectionId: string,
  signal?: AbortSignal,
): Promise<readonly SharePointDriveSelection[]> {
  const response = await request(
    apiBase,
    `${sharePointConnectionPath(connectionId)}/selections`,
    { cache: "no-store", signal },
  );
  const selections = parseSharePointDriveSelectionList(await response.json());
  if (selections.some((selection) => selection.connectionId !== connectionId)) {
    throw new ControlPlaneError(
      "The SharePoint library service returned a selection for another connection.",
    );
  }
  return selections;
}

/**
 * Lists sites the delegated Microsoft account is allowed to see. The query is
 * optional: without one the service returns its first page, which is what the
 * library dialog shows on open. A query only narrows that same list.
 */
export async function listSharePointSites(
  apiBase: string,
  connectionId: string,
  query: string | null,
  signal?: AbortSignal,
): Promise<SharePointSitePage> {
  const normalized = query?.trim() ?? "";
  if (
    normalized.length > 0 &&
    (normalized.length < 2 || normalized.length > 200)
  ) {
    throw new ControlPlaneError(
      "A SharePoint site search needs between 2 and 200 characters.",
    );
  }
  const search =
    normalized.length > 0
      ? `?${new URLSearchParams({ query: normalized }).toString()}`
      : "";
  const response = await request(
    apiBase,
    `${sharePointConnectionPath(connectionId)}/sites${search}`,
    { cache: "no-store", signal },
  );
  return parseSharePointSitePage(await response.json());
}

/** Lists document libraries for one already-authorized SharePoint site. */
export async function getSharePointSiteDrives(
  apiBase: string,
  connectionId: string,
  siteId: string,
  signal?: AbortSignal,
): Promise<readonly SharePointDrive[]> {
  const response = await request(
    apiBase,
    `${sharePointConnectionPath(connectionId)}/sites/${encodeURIComponent(
      sharePointResourceId(siteId, "site identifier"),
    )}/drives`,
    { cache: "no-store", signal },
  );
  const drives = parseSharePointDriveList(await response.json());
  if (drives.some((drive) => drive.siteId !== siteId)) {
    throw new ControlPlaneError(
      "The SharePoint library service returned a drive for another site.",
    );
  }
  return drives;
}

/** Selects one document library; provider object ids remain opaque. */
export async function selectSharePointDrive(
  apiBase: string,
  connectionId: string,
  driveId: string,
  siteId: string,
): Promise<SharePointDriveSelection> {
  const response = await request(
    apiBase,
    `${sharePointConnectionPath(connectionId)}/selections/${encodeURIComponent(
      sharePointResourceId(driveId, "drive identifier"),
    )}`,
    {
      method: "PUT",
      body: JSON.stringify({
        siteId: sharePointResourceId(siteId, "site identifier"),
      }),
    },
  );
  const selection = parseSharePointDriveSelection(await response.json());
  if (
    selection.connectionId !== connectionId ||
    selection.siteId !== siteId ||
    selection.driveId !== driveId
  ) {
    throw new ControlPlaneError(
      "The SharePoint library service returned a different selection.",
    );
  }
  return selection;
}

/** Stops future inventory for one selected library. */
export async function deselectSharePointDrive(
  apiBase: string,
  connectionId: string,
  driveId: string,
): Promise<void> {
  await request(
    apiBase,
    `${sharePointConnectionPath(connectionId)}/selections/${encodeURIComponent(
      sharePointResourceId(driveId, "drive identifier"),
    )}`,
    { method: "DELETE" },
  );
}

function mailFolderConnectionPath(connectionId: string): string {
  return `/v1/control-plane/connections/${encodeURIComponent(
    mailFolderResourceId(connectionId, "connection identifier"),
  )}/mail-folders`;
}

/** Lists the mailbox's folders, with the always-read ones marked as anchors. */
export async function listMailFolders(
  apiBase: string,
  connectionId: string,
  signal?: AbortSignal,
): Promise<MailFolderPage> {
  const response = await request(
    apiBase,
    mailFolderConnectionPath(connectionId),
    {
      cache: "no-store",
      signal,
    },
  );
  return parseMailFolderPage(await response.json());
}

/** Loads the folders this member has explicitly added to the inventory scope. */
export async function getMailFolderSelections(
  apiBase: string,
  connectionId: string,
  signal?: AbortSignal,
): Promise<readonly MailFolderSelection[]> {
  const response = await request(
    apiBase,
    `${mailFolderConnectionPath(connectionId)}/selections`,
    { cache: "no-store", signal },
  );
  const selections = parseMailFolderSelectionList(await response.json());
  if (selections.some((selection) => selection.connectionId !== connectionId)) {
    throw new ControlPlaneError(
      "The mail folder service returned a selection for another connection.",
    );
  }
  return selections;
}

/** Adds one folder to the inventory scope; provider object ids stay opaque. */
export async function selectMailFolder(
  apiBase: string,
  connectionId: string,
  folderId: string,
): Promise<MailFolderSelection> {
  const response = await request(
    apiBase,
    `${mailFolderConnectionPath(connectionId)}/selections/${encodeURIComponent(
      mailFolderResourceId(folderId, "folder identifier"),
    )}`,
    { method: "PUT", body: JSON.stringify({}) },
  );
  const selection = parseMailFolderSelection(await response.json());
  if (
    selection.connectionId !== connectionId ||
    selection.folderId !== folderId
  ) {
    throw new ControlPlaneError(
      "The mail folder service returned a different selection.",
    );
  }
  return selection;
}

/** Stops future inventory for one chosen folder. */
export async function deselectMailFolder(
  apiBase: string,
  connectionId: string,
  folderId: string,
): Promise<void> {
  await request(
    apiBase,
    `${mailFolderConnectionPath(connectionId)}/selections/${encodeURIComponent(
      mailFolderResourceId(folderId, "folder identifier"),
    )}`,
    { method: "DELETE" },
  );
}

export const OPEN_COMMITMENTS_PAGE_SIZE = 20;
export const COMMITMENT_EVIDENCE_PAGE_SIZE = 20;
const MAX_EVIDENCE_IDS_PER_REQUEST = 50;

// The knowledge surface is read-only, but the boundary is a POST envelope, so it
// travels through the same CSRF-protected `request` path as every other
// cookie-authenticated call. `asOf` is the caller-supplied coverage cut-off and
// is never substituted by a wall clock inside this module.
export async function getOpenCommitments(
  apiBase: string,
  params: {
    asOf: string;
    entityId?: string | null;
    cursor?: string | null;
    limit?: number;
  },
  signal?: AbortSignal,
): Promise<OpenCommitmentsResult> {
  const response = await request(
    apiBase,
    "/v1/knowledge/get_open_commitments",
    {
      method: "POST",
      signal,
      body: JSON.stringify({
        asOf: params.asOf,
        entityId: params.entityId ?? null,
        limit: params.limit ?? OPEN_COMMITMENTS_PAGE_SIZE,
        cursor: params.cursor ?? null,
      }),
    },
  );
  return parseOpenCommitmentsResponse(await response.json());
}

export async function getCommitmentEvidence(
  apiBase: string,
  params: {
    asOf: string;
    evidenceIds: readonly string[];
    cursor?: string | null;
    limit?: number;
  },
  signal?: AbortSignal,
): Promise<CommitmentEvidenceResult> {
  const evidenceIds = [...new Set(params.evidenceIds)].slice(
    0,
    MAX_EVIDENCE_IDS_PER_REQUEST,
  );
  if (evidenceIds.length === 0) {
    throw new ControlPlaneError("At least one evidence id is required.");
  }
  const response = await request(apiBase, "/v1/knowledge/get_evidence", {
    method: "POST",
    signal,
    body: JSON.stringify({
      asOf: params.asOf,
      evidenceIds,
      limit: params.limit ?? COMMITMENT_EVIDENCE_PAGE_SIZE,
      cursor: params.cursor ?? null,
    }),
  });
  return parseCommitmentEvidenceResponse(await response.json());
}

export async function startMicrosoftAuthorization(
  apiBase: string,
  connectionId?: string,
): Promise<string> {
  const response = await request(apiBase, "/auth/microsoft/start", {
    method: "POST",
    body: JSON.stringify(
      connectionId
        ? { reauthorization: true, connectionId }
        : { reauthorization: false },
    ),
  });
  const payload: unknown = await response.json();
  if (
    typeof payload !== "object" ||
    payload === null ||
    !("authorizationUrl" in payload) ||
    typeof payload.authorizationUrl !== "string"
  ) {
    throw new ControlPlaneError(
      "The control plane returned an invalid authorization response.",
    );
  }
  return validateMicrosoftAuthorizationUrl(payload.authorizationUrl, apiBase);
}

function validateMicrosoftAuthorizationUrl(
  value: string,
  apiBase: string,
): string {
  let authorizationUrl: URL;
  try {
    authorizationUrl = new URL(value);
  } catch {
    throw new ControlPlaneError(
      "The control plane returned an unsafe authorization URL.",
    );
  }
  const responseTypes = new Set(
    (authorizationUrl.searchParams.get("response_type") ?? "").split(" "),
  );
  const scopes = new Set(
    (authorizationUrl.searchParams.get("scope") ?? "").split(" "),
  );
  const expectedRedirect = new URL(
    "/auth/microsoft/callback",
    `${apiBase}/`,
  ).toString();
  if (
    authorizationUrl.protocol !== "https:" ||
    authorizationUrl.hostname !== "login.microsoftonline.com" ||
    authorizationUrl.username ||
    authorizationUrl.password ||
    authorizationUrl.hash ||
    !/^\/[0-9a-f-]{36}\/oauth2\/v2\.0\/authorize$/i.test(
      authorizationUrl.pathname,
    ) ||
    !responseTypes.has("code") ||
    authorizationUrl.searchParams.get("response_mode") !== "query" ||
    !/^[0-9a-f-]{36}$/i.test(
      authorizationUrl.searchParams.get("client_id") ?? "",
    ) ||
    authorizationUrl.searchParams.get("redirect_uri") !== expectedRedirect ||
    !scopes.has("offline_access") ||
    !scopes.has("Mail.Read") ||
    (authorizationUrl.searchParams.get("state")?.length ?? 0) < 32 ||
    (authorizationUrl.searchParams.get("nonce")?.length ?? 0) < 32 ||
    (authorizationUrl.searchParams.get("code_challenge")?.length ?? 0) < 43 ||
    authorizationUrl.searchParams.get("code_challenge_method") !== "S256" ||
    authorizationUrl.searchParams.has("client_secret")
  ) {
    throw new ControlPlaneError(
      "The control plane returned an unsafe authorization URL.",
    );
  }
  return authorizationUrl.toString();
}
